import React from "react";

import { Box, Skeleton, useMediaQuery, useTheme } from "@mui/material";

export default function EpisodeSkeletonLoader() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const cardMarginX = isMobile ? 0.5 : 2.5;

  return (
    <Box
      className="episode-content"
      sx={{
        flexGrow: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        mx: cardMarginX,
        mt: 0.75,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          px: isMobile ? 1 : 1.2,
          pt: 0.2,
          pb: 1.3,
          gap: 0,
          backgroundColor: "transparent",
        }}
      >
        {/* Left panel skeleton */}
        <Box
          sx={{
            width: isMobile ? "100%" : "56%",
            minWidth: 0,
            height: isMobile ? "52%" : "100%",
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            p: isMobile ? 1 : 1.2,
            [theme.breakpoints.down("sm")]: {
              px: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              height: "100%",
            }}
          >
            {/* Short videos strip */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "primary.light",
                borderRadius: 2,
                px: 1.5,
                py: 1.25,
                backgroundColor: "rgba(37, 99, 235, 0.05)",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Skeleton variant="text" width={120} height={16} />
                <Skeleton variant="rounded" width={72} height={26} />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                }}
              >
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={82} />
                ))}
              </Box>
            </Box>

            {/* Content hub groups/cards */}
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                borderTop: "1px dashed",
                borderColor: "divider",
                pt: 1,
              }}
            >
              <Skeleton
                variant="text"
                width={140}
                height={16}
                sx={{ mb: 0.8 }}
              />
              <Skeleton
                variant="text"
                width={220}
                height={14}
                sx={{ mb: 1.2 }}
              />
              {[0, 1, 2].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={54}
                  sx={{ mb: 1.2, borderRadius: "14px" }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Splitter skeleton */}
        {!isMobile && (
          <Box
            sx={{
              width: 36,
              px: 0.25,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              flexShrink: 0,
              gap: 1,
              mt: "150px",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                borderLeft: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <Skeleton
              variant="rounded"
              width={26}
              height={54}
              sx={{ zIndex: 1 }}
            />
            <Skeleton
              variant="rounded"
              width={26}
              height={54}
              sx={{ zIndex: 1 }}
            />
          </Box>
        )}

        {/* Right panel skeleton */}
        <Box
          sx={{
            width: isMobile ? "100%" : "44%",
            minWidth: 0,
            height: isMobile ? "48%" : "100%",
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            p: isMobile ? 1 : 1.2,
            [theme.breakpoints.down("sm")]: {
              px: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: 1.5,
            }}
          >
            {/* Quick actions */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px",
                px: 2.5,
                pt: 1.5,
                pb: 2,
                flexShrink: 0,
              }}
            >
              <Skeleton
                variant="text"
                width={96}
                height={14}
                sx={{ mb: 1.25 }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton
                  variant="rounded"
                  sx={{ flex: 1, height: 36, borderRadius: "8px" }}
                />
                <Skeleton
                  variant="rounded"
                  sx={{ flex: 1, height: 36, borderRadius: "8px" }}
                />
              </Box>
            </Box>

            {/* Video */}
            <Box
              sx={{
                width: "100%",
                aspectRatio: "16 / 9",
                borderRadius: "12px",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <Skeleton
                variant="rounded"
                width="100%"
                height="100%"
                sx={{ borderRadius: "12px", transform: "none" }}
              />
            </Box>

            {/* Transcript */}
            <Box
              sx={{
                width: "100%",
                minHeight: 320,
                flexGrow: 1,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                p: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pb: 0.75,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  mb: 0.75,
                }}
              >
                <Box>
                  <Skeleton variant="text" width={88} height={18} />
                  <Skeleton variant="text" width={62} height={12} />
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              </Box>
              {[88, 72, 94, 60, 82].map((w) => (
                <Box key={w} sx={{ mb: 1 }}>
                  <Skeleton variant="text" width={`${w}%`} height={14} />
                  <Skeleton
                    variant="text"
                    width={`${Math.max(40, w - 14)}%`}
                    height={14}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
